import {
  composeSDKFactories,
  createElementPropsSDKFactory,
  unsupportedPlayablePropsSDKFactory,
  registerCorvidEvent,
  withValidation,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import { getSlideIndex } from '../utils/getSlideIndex';
import type { ISlideshowOwnSDKFactory } from '../../types';
import {
  indexShouldBePresentInItems,
  validationSchema,
} from './Slideshow.validations';

const slideshowSDKFactory: ISlideshowOwnSDKFactory = api => {
  const { props, setProps, setStyles, getChildren, createSdkState } = api;
  const [state, setState] = createSdkState({
    showNavigationButtons: undefined,
  });

  const [repeaterSdk] = getChildren();
  const getLength = () => repeaterSdk.data.length;

  const setNewIndex = (step: number) => {
    const { loop, currentIndex } = props;
    const newIndex = currentIndex + step;
    setProps({
      currentIndex: getSlideIndex({ newIndex, loop, length: getLength() }),
      navigatingForward: step > 0,
    });
  };

  const { onItemReady, onItemRemoved, forEachItem, forItems } = repeaterSdk;

  return {
    /* current slide */
    get currentIndex() {
      return props.currentIndex;
    },
    changeSlide(newIndex: number) {
      if (!indexShouldBePresentInItems(newIndex, getLength())) {
        return;
      }

      setProps({
        currentIndex: newIndex,
      });
    },
    previous() {
      setNewIndex(-1);
      return Promise.resolve() as any; // TODO: update after viewer part will be implemented
    },
    next() {
      setNewIndex(+1);
      return Promise.resolve() as any; // TODO: update after viewer part will be implemented
    },
    onChange: cb => {
      registerCorvidEvent('onChange', api, cb);
    },

    /* play/pause */
    get isPlaying() {
      return props.isPlaying;
    },
    play() {
      setProps({ isPlaying: true });
    },
    pause() {
      setProps({ isPlaying: false });
    },
    onPlay: cb => {
      registerCorvidEvent('onPlay', api, cb);
    },
    onPause: cb => {
      registerCorvidEvent('onPause', api, cb);
    },

    /* show navigation buttons */
    get showNavigationButtons() {
      return state.showNavigationButtons;
    },
    set showNavigationButtons(value) {
      setState({ showNavigationButtons: value });
      setStyles({
        '--navBtnVisibilityVelo': value ? 'visible' : 'hidden',
      });
    },

    /* repeater sdk */
    get data() {
      return repeaterSdk.data;
    },
    set data(newData: any) {
      repeaterSdk.data = newData;
    },
    onItemReady,
    onItemRemoved,
    forEachItem,
    forItems,
  };
};

export const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableRole: true,
  enableAriaLabel: true,
});

const sdk = composeSDKFactories([
  createElementPropsSDKFactory(),
  unsupportedPlayablePropsSDKFactory,
  withValidation(slideshowSDKFactory, validationSchema),
  accessibilityPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
